import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { LocalizedProductPreview } from './localized-product-preview';

const EASE = [0.32, 0.72, 0, 1] as const;
const IMAGE_TRANSITION = { duration: 0.55, ease: EASE };

/** Propagates idle / hover to children when `animate` changes. */
const cardVariants = {
  idle: {},
  hover: {},
};

/** Only animate bottom, width, height, and radius — same anchor in both states. */
const imageVariants = {
  idle: {
    bottom: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
    transition: IMAGE_TRANSITION,
  },
  hover: {
    bottom: 0,
    width: '100%',
    height: '50%',
    borderRadius: 12,
    transition: IMAGE_TRANSITION,
  },
};

const overlayVariants = {
  idle: { opacity: 0.35, transition: { duration: 0.35 } },
  hover: { opacity: 0, transition: { duration: 0.35 } },
};

const defaultTitleVariants = {
  idle: { opacity: 1, y: 0, visibility: 'visible' as const },
  hover: {
    opacity: 0,
    y: -12,
    visibility: 'hidden' as const,
    transition: { duration: 0.3 },
  },
};

const hoverContentVariants = {
  idle: { opacity: 0, y: 10, visibility: 'hidden' as const },
  hover: {
    opacity: 1,
    y: 0,
    visibility: 'visible' as const,
    transition: { duration: 0.4, delay: 0.15 },
  },
};

const cardBgVariants = {
  idle: { opacity: 0, transition: { duration: 0.45 } },
  hover: { opacity: 1, transition: { duration: 0.45 } },
};

type FeatureRevealCardProps = {
  title: string;
  subtitle?: string;
  description: string;
  image: string;
  imageBackground: string;
  imageAlt?: string;
  className?: string;
};

export const FeatureRevealCard = ({
  title,
  subtitle,
  description,
  image,
  imageBackground,
  imageAlt = '',
  className,
}: FeatureRevealCardProps) => {
  const locale = useLocale();

  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      animate={isHovered ? 'hover' : 'idle'}
      className={cn(
        'relative flex cursor-default flex-col overflow-hidden rounded-2xl border border-landing-card-border bg-[#1a2340]',
        'h-[640px]',
        className,
      )}
      initial="idle"
      onHoverEnd={() => {
        setIsHovered(false);
      }}
      onHoverStart={() => {
        setIsHovered(true);
      }}
      variants={cardVariants}
    >
      {/* Light bg layer that fades in on hover */}
      <motion.div
        className="pointer-events-none absolute inset-0 z-0 bg-landing-card-bg"
        variants={cardBgVariants}
      />

      {/* Image — full bleed when idle, shrinks toward bottom on hover */}
      <motion.div
        className="absolute left-1/2 z-10 -translate-x-1/2 overflow-hidden p-4"
        variants={imageVariants}
      >
        {locale === 'fa' ? (
          <LocalizedProductPreview title={title} />
        ) : (
          <img
            alt={imageAlt || title}
            className="h-full w-full rounded-lg object-cover"
            src={image}
            style={{
              background: imageBackground,
            }}
          />
        )}
        {/* Subtle dark tint over image — fades out when card has light bg */}
        <motion.div
          className="pointer-events-none absolute inset-0 bg-[#1a2340] p-4"
          variants={overlayVariants}
        />
      </motion.div>

      {/* Prominent title only — visible when not hovering */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 p-7"
        variants={defaultTitleVariants}
      >
        <h3 className="font-bold font-libre-baskerville text-4xl text-white leading-tight tracking-tight drop-shadow-md md:text-[54px]">
          {title}
        </h3>
      </motion.div>

      {/* Title, subtitle, and description — visible on hover */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 p-7"
        variants={hoverContentVariants}
      >
        <h3 className="mb-2 font-bold font-libre-baskerville text-3xl text-landing-heading-sm leading-tight tracking-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="mb-3 font-libre-baskerville font-semibold text-base text-landing-heading-sm leading-snug">
            {subtitle}
          </p>
        )}
        <p className="text-landing-body text-sm leading-relaxed">{description}</p>
      </motion.div>
    </motion.div>
  );
};
